import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Button,
  Tooltip,
  Dropdown,
  Menu,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
} from "antd";
import {
  SearchOutlined,
  ClearOutlined,
  MoreOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { RxStar } from "react-icons/rx";

import PageHeader from "../../../components/pageheader/PageHeader";
import EmptyState from "../../../components/emptystate/EmptyState";
import {
  deleteAccumulationUnit,
  getAccumulationUnits,
  updateAccumulationUnit,
} from "../../../api/Services";
import { hasPermission, privilegesEnum } from "../../../helpers/PermissionHelper";
import styles from "./UnitsMockup.module.scss";

const UnitsMockup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBasePath = () => {
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/units";
    }
    return "/commercial-intelligence/configuration/units";
  };

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const [nameFilterValue, setNameFilterValue] = useState("");
  const [codeFilterValue, setCodeFilterValue] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any | null>(null);
  const [viewingRecord, setViewingRecord] = useState<any | null>(null);

  const [editForm] = Form.useForm();

  const fetchAccumulationUnits = async (pagination = actualPage) => {
    setLoading(true);
    const response = await getAccumulationUnits(pagination, {
      name: nameFilterValue || undefined,
      code: codeFilterValue || undefined,
    });
    setData(response.accumulationUnits);
    setTotalSize(response.totalElements);
    setLoading(false);
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_REWARDS)) {
      fetchAccumulationUnits(actualPage);
    } else {
      setLoading(false);
    }
  }, [actualPage, nameFilterValue, codeFilterValue]);

  const handlePagination = (page: number) => {
    setActualPage(page);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setCodeFilterValue("");
    setActualPage(1);
  };

  const hasActiveFilters = () => {
    return nameFilterValue !== "" || codeFilterValue !== "";
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="AccumulationUnit" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="AccumulationUnit"
        />
      );
    }

    return null;
  };

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const ok = await deleteAccumulationUnit(id);
    if (!ok) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchAccumulationUnits(actualPage);
    setLoading(false);
  };

  const openEditModal = (record: any) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      name: record.name,
      code: record.code,
      baseAccumulationFactor: record.baseAccumulationFactor,
      specificAccumulationFactor: record.specificAccumulationFactor,
      accumulationUnitValue: record.accumulationUnitValue,
      useAccumulationForPayment: record.useAccumulationForPayment,
      useClientTiers: record.useClientTiers,
    });
    setEditModalOpen(true);
  };

  const submitEdit = async () => {
    if (!editingRecord) return;
    try {
      const values = await editForm.validateFields();
      setLoading(true);
      const ok = await updateAccumulationUnit(editingRecord.id, {
        name: values.name,
        code: values.code,
        baseAccumulationFactor: values.baseAccumulationFactor,
        specificAccumulationFactor: values.specificAccumulationFactor,
        accumulationUnitValue: values.accumulationUnitValue,
        useAccumulationForPayment: booleanOrFalse(values.useAccumulationForPayment),
        useClientTiers: booleanOrFalse(values.useClientTiers),
      });
      if (ok) {
        message.success(t("Common-EditSuccessMessage"));
        setEditModalOpen(false);
        setEditingRecord(null);
        await fetchAccumulationUnits(actualPage);
      } else message.error(t("Common-EditErrorMessage"));
    } catch (e) {
      // validation
    }
    setLoading(false);
  };

  const openViewModal = (record: any) => {
    setViewingRecord(record);
    setViewModalOpen(true);
  };

  const booleanOrFalse = (v: unknown) => Boolean(v);

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <button
          type="button"
          className={styles.nameLink}
          onClick={() => openViewModal(record)}
        >
          {name}
        </button>
      ),
    },
    {
      title: t("Common-Code"),
      dataIndex: "code",
    },
    {
      title: "Factor de acumulación base",
      dataIndex: "baseAccumulationFactor",
      render: (value: number) => value?.toLocaleString("es-ES") || "-",
    },
    {
      title: "Factor de acumulación específico",
      dataIndex: "specificAccumulationFactor",
      render: (value: number) => value?.toLocaleString("es-ES") || "-",
    },
    {
      title: "Valor de la unidad de acumulación",
      dataIndex: "accumulationUnitValue",
      render: (value: number) => value?.toLocaleString("es-ES") || "-",
    },
    {
      title: "Usa acumulación para pagar compras",
      dataIndex: "useAccumulationForPayment",
      render: (value: boolean) => (
        <span style={{ color: value ? "#52c41a" : "#ff4d4f" }}>
          {value ? t("Common-Yes") : t("Common-No")}
        </span>
      ),
    },
    {
      title: "Usa tiers de clientes",
      dataIndex: "useClientTiers",
      render: (value: boolean) => (
        <span style={{ color: value ? "#52c41a" : "#ff4d4f" }}>
          {value ? t("Common-Yes") : t("Common-No")}
        </span>
      ),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (_text: unknown, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => openViewModal(record)}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_REWARDS) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(record)}
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_REWARDS) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const canRead = hasPermission(privilegesEnum.READ_REWARDS);

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("NavBar-Units")}
        icon={<RxStar />}
        onCreateClick={() => navigate(`${getBasePath()}/create`)}
        createPermission={privilegesEnum.WRITE_REWARDS}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setNameFilterValue(e.target.value);
                    setActualPage(1);
                  }}
                  value={nameFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-Code")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setCodeFilterValue(e.target.value);
                    setActualPage(1);
                  }}
                  value={codeFilterValue}
                />
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          {canRead && (
            <Table
              size="small"
              className={styles.table}
              loading={loading}
              columns={columns}
              dataSource={data}
              rowKey={(record) => record.id}
              pagination={{
                current: actualPage,
                total: totalSize,
                pageSize: 10,
                showSizeChanger: false,
                onChange: handlePagination,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
              }}
              locale={{
                emptyText: getEmptyState(),
              }}
            />
          )}
        </div>
      </div>

      <Modal
        title={t("Common-Edit")}
        visible={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingRecord(null);
        }}
        onOk={submitEdit}
        okText={t("Common-Save")}
        destroyOnClose
        width={520}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label={t("Common-Name")}
            name="name"
            rules={[{ required: true, message: t("Common-Name") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("Common-Code")}
            name="code"
            rules={[{ required: true, message: t("Common-Code") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Factor de acumulación base" name="baseAccumulationFactor">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Factor de acumulación específico" name="specificAccumulationFactor">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Valor de la unidad de acumulación" name="accumulationUnitValue">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Usa acumulación para pagar compras"
            name="useAccumulationForPayment"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Usa tiers de clientes" name="useClientTiers" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("Common-View")}
        visible={viewModalOpen}
        onCancel={() => {
          setViewModalOpen(false);
          setViewingRecord(null);
        }}
        footer={[
          <Button key="close" onClick={() => setViewModalOpen(false)}>
            {t("Common-Close")}
          </Button>,
        ]}
        width={520}
      >
        {viewingRecord && (
          <div className={styles.viewDetail}>
            <p>
              <strong>{t("Common-Name")}:</strong> {viewingRecord.name}
            </p>
            <p>
              <strong>{t("Common-Code")}:</strong> {viewingRecord.code}
            </p>
            <p>
              <strong>Factor de acumulación base:</strong>{" "}
              {viewingRecord.baseAccumulationFactor?.toLocaleString("es-ES") ?? "-"}
            </p>
            <p>
              <strong>Factor de acumulación específico:</strong>{" "}
              {viewingRecord.specificAccumulationFactor?.toLocaleString("es-ES") ?? "-"}
            </p>
            <p>
              <strong>Valor de la unidad de acumulación:</strong>{" "}
              {viewingRecord.accumulationUnitValue?.toLocaleString("es-ES") ?? "-"}
            </p>
            <p>
              <strong>Usa acumulación para pagar compras:</strong>{" "}
              {viewingRecord.useAccumulationForPayment ? t("Common-Yes") : t("Common-No")}
            </p>
            <p>
              <strong>Usa tiers de clientes:</strong>{" "}
              {viewingRecord.useClientTiers ? t("Common-Yes") : t("Common-No")}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UnitsMockup;
